import { yupResolver } from "@hookform/resolvers/yup";
import {
  CardItemConfig,
  DEFAULT_SECTION_MARGINS,
  HeadingTag,
  SectionMargins,
  VideoContentCardsSectionContent,
} from "api/shop";
import { CourseGroupAttributeType, useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import {
  HeadingTagSelect,
  IDropdownOption,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
} from "components/atoms";
import { PaziaDropdown } from "components/atoms/PaziaDropdown/PaziaDropdown";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { PaziaQuill } from "components/atoms/PaziaQuill/PaziaQuill";
import { Select } from "components/atoms/PaziaSelect/PaziaSelect";
import { HEADING_TAG_OPTIONS } from "components/atoms/HeadingTagSelect/HeadingTagSelect";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useEffect } from "react";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

interface VideoContentCardsFormProps {
  onSubmit: (data: VideoContentCardsFormValues) => void;
  onCancel: () => void;
  defaultValues?: VideoContentCardsSectionContent;
  isLoading?: boolean;
}

const HEADER_ALIGN_OPTIONS: IDropdownOption[] = [
  { value: "left", label: "Lewo" },
  { value: "center", label: "Środek" },
  { value: "right", label: "Prawo" },
];

export const VideoContentCardsForm = ({
  onSubmit,
  onCancel,
  defaultValues,
  isLoading,
}: VideoContentCardsFormProps) => {
  const { allAttributes, isLoading: loadingAttributes } = useCourseGroupAttributesAll();
  const textAttributes = allAttributes.filter((a) => a.attributeType === CourseGroupAttributeType.Text);
  const wysiwygAttributes = allAttributes.filter((a) => a.attributeType === CourseGroupAttributeType.Wysiwyg);
  const textAttributeOptions = textAttributes.map((a) => ({ value: a.id, label: a.name }));
  const wysiwygAttributeOptions = wysiwygAttributes.map((a) => ({ value: a.id, label: a.name }));

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      animation: defaultValues?.animation ?? "fade-bottom",
      headerText: defaultValues?.header?.text ?? "",
      headerTag: defaultValues?.header?.tag ?? "h2",
      headerAlign: defaultValues?.header?.align ?? "left",
      videoUrl: defaultValues?.videoUrl ?? "",
      videoSourceMode: defaultValues?.videoUrlAttributeId != null ? "attribute" : "manual",
      videoUrlAttributeId: defaultValues?.videoUrlAttributeId ?? null,
      contentData: defaultValues?.content?.data ?? "",
      contentSourceMode: defaultValues?.contentAttributeId != null ? "attribute" : "manual",
      contentAttributeId: defaultValues?.contentAttributeId ?? null,
      cards:
        defaultValues?.cards?.map((c) => ({
          header: c.header,
          headerTag: c.headerTag ?? "h3",
          content: c.content,
          bgColor: c.bgColor,
          textColor: c.textColor,
          attributeId: c.attributeId ?? null,
        })) ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });

  const { handleSubmit, control, setValue } = methods;

  const videoSourceMode = useWatch({ control, name: "videoSourceMode" });
  const contentSourceMode = useWatch({ control, name: "contentSourceMode" });

  const { fields, append, remove } = useFieldArray({ control, name: "cards" });

  useEffect(() => {
    if (
      contentSourceMode === "attribute" &&
      methods.getValues("contentAttributeId") == null &&
      wysiwygAttributeOptions.length > 0
    ) {
      setValue("contentAttributeId", wysiwygAttributeOptions[0].value as number);
    }
  }, [contentSourceMode, wysiwygAttributeOptions.length]);

  useEffect(() => {
    if (
      videoSourceMode === "attribute" &&
      methods.getValues("videoUrlAttributeId") == null &&
      textAttributeOptions.length > 0
    ) {
      setValue("videoUrlAttributeId", textAttributeOptions[0].value as number);
    }
  }, [videoSourceMode, textAttributeOptions.length]);

  const updateCard = (index: number, field: keyof FormCardItem, value: string | number | undefined | null) => {
    methods.setValue(`cards.${index}.${field}` as any, value);
  };

  const handleFormSubmit = (data: FormValues) => {
    const headerText = data.headerText?.trim() ?? "";
    const isVideoAttributeMode = data.videoSourceMode === "attribute";
    const isContentAttributeMode = data.contentSourceMode === "attribute";
    const trimmedContentData = data.contentData?.trim() ?? "";

    const formatted: VideoContentCardsFormValues = {
      animation: data.animation,
      header: headerText
        ? {
            text: headerText,
            tag: (data.headerTag as HeadingTag) || undefined,
            align: (data.headerAlign as "left" | "center" | "right") || undefined,
          }
        : undefined,
      ...(isVideoAttributeMode
        ? { videoUrlAttributeId: data.videoUrlAttributeId ?? undefined }
        : { videoUrl: data.videoUrl || undefined }),
      ...(isContentAttributeMode
        ? { contentAttributeId: data.contentAttributeId ?? undefined }
        : trimmedContentData
          ? { content: { data: data.contentData } }
          : {}),
      ...(data.cards.length > 0
        ? {
            cards: data.cards.map((c) => ({
              header: c.header,
              headerTag: c.headerTag as any,
              content: c.content,
              bgColor: c.bgColor,
              textColor: c.textColor,
              ...(c.attributeId ? { attributeId: c.attributeId } : {}),
            })),
          }
        : {}),
      margins: data.margins,
    };
    onSubmit(formatted);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-2">
          <PaziaSelect name="animation" label="Animacja" required options={ANIMATION_OPTIONS} />
          <PaziaInput name="headerText" label="Nagłówek (opcjonalnie)" />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <PaziaDropdown
            name="headerTag"
            label="Tag nagłówka"
            options={HEADING_TAG_OPTIONS as IDropdownOption[]}
          />
          <PaziaDropdown name="headerAlign" label="Wyrównanie nagłówka" options={HEADER_ALIGN_OPTIONS} />
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Źródło filmu (opcjonalnie)</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => {
                  setValue("videoSourceMode", "manual");
                  setValue("videoUrlAttributeId", null);
                }}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  videoSourceMode === "manual"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Ręcznie
              </button>
              <button
                type="button"
                onClick={() => {
                  setValue("videoSourceMode", "attribute");
                  setValue("videoUrl", "");
                }}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  videoSourceMode === "attribute"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Atrybut jako źródło
              </button>
            </div>
          </div>

          {videoSourceMode === "manual" ? (
            <PaziaInput
              name="videoUrl"
              label="Link do filmu YouTube (opcjonalnie)"
              placeholder="https://www.youtube.com/watch?v=..."
            />
          ) : (
            <>
              {textAttributes.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Brak atrybutów tekstowych</p>
              ) : (
                <PaziaSelect
                  name="videoUrlAttributeId"
                  label="Atrybut tekstowy (opcjonalnie)"
                  options={textAttributeOptions}
                />
              )}
            </>
          )}
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Źródło treści (opcjonalnie)</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => {
                  setValue("contentSourceMode", "manual");
                  setValue("contentAttributeId", null);
                }}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  contentSourceMode === "manual"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Treść ręczna
              </button>
              <button
                type="button"
                onClick={() => {
                  setValue("contentSourceMode", "attribute");
                  setValue("contentData", "");
                }}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  contentSourceMode === "attribute"
                    ? "bg-indigo-600 text-white"
                    : "border border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Atrybut jako źródło
              </button>
            </div>
          </div>

          {contentSourceMode === "manual" ? (
            <PaziaFromQuill name="contentData" label="Treść" />
          ) : (
            <>
              {wysiwygAttributes.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">Brak atrybutów WYSIWYG</p>
              ) : (
                <PaziaSelect
                  name="contentAttributeId"
                  label="Atrybut WYSIWYG"
                  options={wysiwygAttributeOptions}
                />
              )}
            </>
          )}
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Karty</h4>
            <button
              type="button"
              onClick={() =>
                append({
                  header: "",
                  headerTag: "h3",
                  content: "",
                  bgColor: "#f1f5f9",
                  textColor: "#1e293b",
                  attributeId: null,
                })
              }
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj kartę
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak kart. Kliknij &quot;Dodaj kartę&quot;, aby dodać.
            </p>
          ) : (
            <div className="space-y-4">
              {fields.map((field, index) => {
                const card = methods.watch(`cards.${index}`);
                const isAttribute = !!card?.attributeId;

                return (
                  <div
                    key={field.id}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        Karta {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        title="Usuń kartę"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                    <div className="grid gap-3">
                      <div>
                        <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Tryb</label>
                        <select
                          value={isAttribute ? "attribute" : "static"}
                          onChange={(e) => {
                            if (e.target.value === "static") {
                              updateCard(index, "attributeId", null);
                            } else {
                              updateCard(index, "attributeId", allAttributes[0]?.id ?? -1);
                            }
                          }}
                          className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                        >
                          <option value="static">Statyczna</option>
                          <option value="attribute">Atrybut</option>
                        </select>
                      </div>

                      {isAttribute && (
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Atrybut</label>
                          <Select
                            name={`card-attr-${index}`}
                            options={allAttributes.map((a) => ({ value: a.id, label: a.name }))}
                            value={card.attributeId ?? ""}
                            isClearable={false}
                            isLoading={loadingAttributes}
                            onChange={(opt: IDropdownOption | null) => {
                              updateCard(index, "attributeId", opt ? Number(opt.value) : null);
                            }}
                          />
                        </div>
                      )}

                      <PaziaInput
                        name={`cards.${index}.header`}
                        label={isAttribute ? "Nagłówek (opcjonalny, domyślnie nazwa atrybutu)" : "Nagłówek"}
                        required={!isAttribute}
                      />
                      <HeadingTagSelect name={`cards.${index}.headerTag`} label="Tag nagłówka karty" />

                      <div className="flex gap-3">
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Kolor tła</label>
                          <input
                            type="color"
                            {...methods.register(`cards.${index}.bgColor`)}
                            className="h-8 w-12 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                            Kolor tekstu
                          </label>
                          <input
                            type="color"
                            {...methods.register(`cards.${index}.textColor`)}
                            className="h-8 w-12 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                          />
                        </div>
                      </div>

                      {!isAttribute && (
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Treść</label>
                          <PaziaQuill
                            value={methods.watch(`cards.${index}.content`)}
                            onChange={(html) => methods.setValue(`cards.${index}.content`, html)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const cardSchema = yup.object({
  header: yup.string().default(""),
  headerTag: yup.string(),
  content: yup.string().default(""),
  bgColor: yup.string().required("Kolor tła jest wymagany").default("#f1f5f9"),
  textColor: yup.string().required("Kolor tekstu jest wymagany").default("#1e293b"),
  attributeId: yup.number().optional().nullable(),
});

const schema = yup
  .object({
    animation: yup.string().required("Animacja jest wymagana"),
    headerText: yup.string().optional(),
    headerTag: yup.string().optional(),
    headerAlign: yup.string().optional(),
    videoSourceMode: yup.string().required(),
    videoUrl: yup.string().optional(),
    videoUrlAttributeId: yup.number().nullable(),
    contentSourceMode: yup.string().required(),
    contentData: yup.string().when("contentSourceMode", {
      is: "manual",
      then: (s) => s.optional(),
      otherwise: (s) => s.optional(),
    }),
    contentAttributeId: yup.number().nullable(),
    cards: yup.array().of(cardSchema),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormCardItem {
  header: string;
  headerTag?: string;
  content: string;
  bgColor: string;
  textColor: string;
  attributeId?: number | null;
}

interface FormValues {
  animation: string;
  headerText: string;
  headerTag: string;
  headerAlign: string;
  videoSourceMode: string;
  videoUrl: string;
  videoUrlAttributeId: number | null;
  contentSourceMode: string;
  contentData: string;
  contentAttributeId: number | null;
  cards: FormCardItem[];
  margins: SectionMargins;
}

export interface VideoContentCardsFormValues {
  animation: string;
  header?: {
    text: string;
    tag?: HeadingTag;
    align?: "left" | "center" | "right";
  };
  videoUrl?: string;
  videoUrlAttributeId?: number;
  content?: { data: string };
  contentAttributeId?: number;
  cards?: CardItemConfig[];
  margins?: SectionMargins;
}
